import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle, Search, ExternalLink } from 'lucide-react';

const Certifications = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const certifiedUsers = [
    {
      username: '@bernice',
      fullName: 'Orjiugo Bernice',
      certificateId: 'CERT-2024-001',
      dateIssued: '2024-01-15',
      status: 'Verified',
      certificateUrl: '/confirm/bernice',
      image: '/confirm/bernice/orjiugobernicecertificate.png'
    },
    {
      username: '@cornerstone22',
      fullName: 'Uduak Effiong Joe',
      certificateId: 'CERT-2024-002',
      dateIssued: '2024-01-20',
      status: 'Verified',
      certificateUrl: '/confirm/cornerstone22',
      image: '/confirm/cornerstone22/uduakeffiongjoecertificate.png'
    },
    {
      username: '@gbemi2024',
      fullName: 'Aladetoyinbo Gbemisola Ibiere',
      certificateId: 'CERT-2024-003',
      dateIssued: '2024-02-05',
      status: 'Verified',
      certificateUrl: '/confirm/gbemi2024',
      image: '/confirm/gbemi2024/AladetoyinboGbemisolaIbiereCertificate.png'
    },
    {
      username: '@gedu',
      fullName: 'Gedu Abdulmumin',
      certificateId: 'CERT-2024-004',
      dateIssued: '2024-02-10',
      status: 'Verified',
      certificateUrl: '/confirm/gedu',
      image: '/confirm/gedu/geduabdulmumincertificate.jpeg'
    },
    {
      username: '@samson',
      fullName: 'Orjiugo Samson',
      certificateId: 'CERT-2024-005',
      dateIssued: '2024-02-12',
      status: 'Verified',
      certificateUrl: '/confirm/samson',
      image: '/confirm/samson/orjiugosamsoncertificate.png'
    }
  ];

  const filteredCertifications = certifiedUsers.filter(cert =>
    cert.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cert.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cert.certificateId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewCertificate = (certificateUrl) => {
    window.open(certificateUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50">

      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-700 hover:text-slate-900">
              <ArrowLeft size={20} />
              <span className="font-medium">Back to Home</span>
            </button>
            <h1 className="text-lg font-semibold text-slate-900">Verified Certifications</h1>
            <div className="w-32"></div>
          </div>
        </div>
      </header>

      <section className="bg-gradient-to-br from-blue-600 to-purple-600 py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="text-white" size={40} />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">Verified User Certifications</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Browse and verify authentic MODANMIC user certificates. All certifications are issued and verified by our platform.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search by username, name, or certificate ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-lg"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
              <Shield size={20} className="text-blue-600" />
            </div>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Total Verified</p>
            <p className="text-2xl font-bold text-slate-900">{certifiedUsers.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Active Status</p>
            <p className="text-2xl font-bold text-green-600">100%</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
              <Shield size={20} className="text-purple-600" />
            </div>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Security Level</p>
            <p className="text-2xl font-bold text-slate-900">High</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
              <ExternalLink size={20} className="text-orange-600" />
            </div>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Available</p>
            <p className="text-2xl font-bold text-slate-900">{certifiedUsers.length}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCertifications.length > 0 ? (
            filteredCertifications.map((cert, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div
                  className="relative h-52 bg-gradient-to-br from-slate-100 to-slate-50 overflow-hidden cursor-pointer group"
                  onClick={() => handleViewCertificate(cert.certificateUrl)}
                >
                  <img
                    src={cert.image}
                    alt={cert.fullName + ' certificate'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="absolute inset-0 hidden items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
                    <Shield className="text-slate-300" size={64} />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                    <span className="text-white text-sm font-semibold flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                      <ExternalLink size={14} /> Open Certificate
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <CheckCircle size={11} /> {cert.status}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 leading-tight">{cert.fullName}</h3>
                      <p className="text-sm text-blue-600 font-medium mt-0.5">{cert.username}</p>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={16} className="text-green-600" />
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-3 mb-4 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-medium">Certificate ID</span>
                      <span className="font-bold text-slate-700">{cert.certificateId}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-medium">Date Issued</span>
                      <span className="font-semibold text-slate-700">{cert.dateIssued}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleViewCertificate(cert.certificateUrl)}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                  >
                    <ExternalLink size={15} />
                    View Certificate
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-20">
              <Search className="mx-auto mb-4 text-slate-300" size={64} />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Results Found</h3>
              <p className="text-slate-500">Try adjusting your search query</p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-100 rounded-2xl p-8">
          <h3 className="text-xl font-bold text-slate-900 mb-6">About Certificate Verification</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">How to Verify</h4>
              <ol className="space-y-2.5 text-sm text-slate-700">
                <li className="flex gap-3 items-start">
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                  <span>Click "View Certificate" on any user card</span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                  <span>Review the official certificate page</span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                  <span>Verify the certificate ID matches</span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                  <span>Check the verification status badge</span>
                </li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Certificate Benefits</h4>
              <ul className="space-y-2.5 text-sm text-slate-700">
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-500 flex-shrink-0" /><span>Verified user identity</span></li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-500 flex-shrink-0" /><span>Enhanced account security</span></li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-500 flex-shrink-0" /><span>Increased transaction limits</span></li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-500 flex-shrink-0" /><span>Priority customer support</span></li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Certifications;
